import React, { useState, useEffect, useCallback } from 'react';
import { StoresPage, Store } from './StoresPage/StoresPage';
import { EditStorePage } from './EditStorePage/EditStorePage';
import { SettingsNativeBridgeFactory } from '../../bridge/SettingsNativeBridgeFactory';
import { ACCOUNT_MANAGEMENT_SETTING_IDS } from '../../config/SettingIds';
import { GetSettingsResponse } from '../../Contracts/responses';
import { SettingsCategory } from '../../config/SettingsCategory';

type StoreView = 'StoreList' | 'EditStore';

interface AccountManagementPageProps {
	title: string;
	description?: string;
	categoryId?: string;
	onNavigate?: (category: SettingsCategory) => void;
}

export const AccountManagementPage: React.FC<AccountManagementPageProps> = ({
	categoryId,
	onNavigate,
}) => {
	const isAddStoreTrigger = categoryId === SettingsCategory.AddStore;
	const [view, setView] = useState<StoreView>('StoreList');
	const [editingStore, setEditingStore] = useState<Store | null>(null);
	const [stores, setStores] = useState<Store[]>([]);

	const loadStores = useCallback(async () => {
		try {
			const bridge = SettingsNativeBridgeFactory.getBridge();
			const response = await bridge.getSettings({
				settings: [{ settingKey: ACCOUNT_MANAGEMENT_SETTING_IDS.STORES_LIST }],
			});
			if (response && response.status === 0) {
				const settingsResp = response as GetSettingsResponse;
				const setting = settingsResp.data.settings?.[0];
				if (setting?.settingValue) {
					const parsed: Store[] = JSON.parse(setting.settingValue as string);
					setStores(parsed);
				}
			}
		} catch {
			// Bridge unavailable or parse error — keep current list
		}
	}, []);

	// Load stores on mount (only relevant when showing the list, not the trigger)
	useEffect(() => {
		if (isAddStoreTrigger) return;
		loadStores();
	}, [isAddStoreTrigger, loadStores]);

	// Re-fetch stores when native signals that the list has changed (e.g. after Add Account)
	useEffect(() => {
		if (isAddStoreTrigger) return;
		window.addEventListener('account-stores-updated', loadStores);
		return () => {
			window.removeEventListener('account-stores-updated', loadStores);
		};
	}, [isAddStoreTrigger, loadStores]);

	// When rendered as "Add store" sidebar item — fire trigger immediately
	useEffect(() => {
		if (!isAddStoreTrigger) return;

		const fireAddStoreTrigger = async () => {
			try {
				const bridge = SettingsNativeBridgeFactory.getBridge();
				await bridge.setSetting({
					settingKey: ACCOUNT_MANAGEMENT_SETTING_IDS.ADD_STORE,
					settingValue: true,
				});
			} catch {
				// Bridge unavailable
			}
			// Navigate back to Stores list; native handles the Add Account UI
			onNavigate?.(SettingsCategory.AccountManagement);
		};

		fireAddStoreTrigger();
		// onNavigate intentionally excluded: it's a new arrow function on every SettingsLayout render
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isAddStoreTrigger]);

	const handleEdit = (store: Store) => {
		setEditingStore(store);
		setView('EditStore');
	};

	const handleBack = () => {
		setView('StoreList');
		setEditingStore(null);
	};

	const handleSave = async (updated: Store) => {
		setStores((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
		try {
			const bridge = SettingsNativeBridgeFactory.getBridge();
			await bridge.setSetting({
				settingKey: ACCOUNT_MANAGEMENT_SETTING_IDS.EDIT_STORE,
				settingValue: JSON.stringify({
					id: updated.id,
					name: updated.name,
					description: updated.description ?? '',
				}),
			});
		} catch {
			// Bridge unavailable — local state already updated
		}
		handleBack();
	};

	const handleToggle = async (storeId: string, checked: boolean) => {
		setStores((prev) =>
			prev.map((s) => (s.id === storeId ? { ...s, showInMenu: checked } : s))
		);
		try {
			const bridge = SettingsNativeBridgeFactory.getBridge();
			await bridge.setSetting({
				settingKey: ACCOUNT_MANAGEMENT_SETTING_IDS.EDIT_STORE,
				settingValue: JSON.stringify({ id: storeId, showInMenu: checked }),
			});
		} catch {
			// Bridge unavailable — local state already updated
		}
	};

	const handleRemove = async (storeId: string) => {
		setStores((prev) => prev.filter((s) => s.id !== storeId));
		try {
			const bridge = SettingsNativeBridgeFactory.getBridge();
			await bridge.setSetting({
				settingKey: ACCOUNT_MANAGEMENT_SETTING_IDS.REMOVE_STORE,
				settingValue: JSON.stringify({ id: storeId }),
			});
		} catch {
			// Bridge unavailable — local state already updated
		}
	};

	// When in add-trigger mode, render nothing — native takes over immediately
	if (isAddStoreTrigger) {
		return null;
	}

	if (view === 'EditStore' && editingStore) {
		return <EditStorePage store={editingStore} onBack={handleBack} onSave={handleSave} />;
	}

	return (
		<StoresPage
			stores={stores}
			onEdit={handleEdit}
			onToggle={handleToggle}
			onRemove={handleRemove}
		/>
	);
};
